import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCloudUpload, 
  faTrash, 
  faStar, 
  faImage,
  faSpinner,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import styles from './EPKBuilder.module.css';

const PhotosStep = ({ data, updateData, errors, mode, fileStorageRef }) => {
  // Initialize state from props only once
  const [photos, setPhotos] = useState(() => data.photos || []);
  const [logo, setLogo] = useState(() => data.logo || null);
  const [thumbnail, setThumbnail] = useState(() => data.thumbnail || null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [draggedPhotoIndex, setDraggedPhotoIndex] = useState(null);
  
  const photoInputRef = useRef(null);
  const logoInputRef = useRef(null);
  
  // Use refs to prevent update loops
  const initialRender = useRef(true);
  const prevPhotosRef = useRef(photos);
  const prevLogoRef = useRef(logo);
  const prevThumbnailRef = useRef(thumbnail);

  // Cleanup object URLs when component unmounts
  useEffect(() => {
    return () => {
      // Cleanup any object URLs to prevent memory leaks
      if (Array.isArray(photos)) {
        photos.forEach(photo => {
          if (photo?.url && photo.url.startsWith('blob:') && !photo.needsUpload) {
            URL.revokeObjectURL(photo.url);
          }
        });
      }
      
      if (logo && logo.url && logo.url.startsWith('blob:') && !logo.needsUpload) {
        URL.revokeObjectURL(logo.url);
      }
    };
  }, [photos, logo]);

  // Sync with parent component only when data actually changes
  useEffect(() => {
    // Skip the update on the initial render
    if (initialRender.current) {
      initialRender.current = false;
      prevPhotosRef.current = photos;
      prevLogoRef.current = logo;
      prevThumbnailRef.current = thumbnail;
      return;
    }
    
    // Only update if data has actually changed
    if (JSON.stringify(prevPhotosRef.current) !== JSON.stringify(photos) || 
        JSON.stringify(prevLogoRef.current) !== JSON.stringify(logo) ||
        JSON.stringify(prevThumbnailRef.current) !== JSON.stringify(thumbnail)) {
      
      console.log('PhotosStep: Syncing with parent component', { photos, logo, thumbnail });
      updateData({
        photos,
        logo,
        thumbnail
      });
      
      // Update refs
      prevPhotosRef.current = photos;
      prevLogoRef.current = logo;
      prevThumbnailRef.current = thumbnail;
    }
  }, [photos, logo, thumbnail, updateData]);
  
  const triggerPhotoUpload = () => {
    if (mode === 'view') return;
    photoInputRef.current.click();
  };
  
  const triggerLogoUpload = () => {
    if (mode === 'view') return;
    logoInputRef.current.click();
  };
  
  // Modified to store files in fileStorageRef
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Check if photos array will exceed 5 photos
    if (photos.length + files.length > 5) {
      setUploadError('You can upload a maximum of 5 photos. Please remove some photos first.');
      return;
    }
    
    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);
    
    const uploadedPhotos = [];
    const totalFiles = files.length;
    let filesProcessed = 0;
    
    try {
      for (const file of files) {
        // Validate file size (max 3MB)
        if (file.size > 3 * 1024 * 1024) {
          throw new Error(`File "${file.name}" exceeds the 3MB size limit`);
        }
        
        // Validate file type - only allow images
        if (!file.type.startsWith('image/')) {
          throw new Error(`File "${file.name}" is not a valid image`);
        }
        
        // Compress image before creating local URL
        const compressedFile = await compressImage(file);
        
        // Create a local URL for the file
        const objectUrl = URL.createObjectURL(compressedFile);
        
        // Create a thumbnail version using canvas
        const thumbnailUrl = await createThumbnail(compressedFile);
        
        // Generate a unique file ID
        const fileId = `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // Store the actual file in the fileStorageRef
        fileStorageRef.current.photos[fileId] = compressedFile;
        
        // Create photo object with reference to the file ID
        const photoData = {
          id: fileId,
          fileId: fileId, // Reference to the stored file
          url: objectUrl,
          thumbnailUrl: thumbnailUrl,
          fileName: file.name,
          fileType: 'image/jpeg', // Always convert to JPEG for consistency
          uploadedAt: new Date().toISOString(),
          needsUpload: true // Flag to indicate this needs to be uploaded to server
        };
        
        uploadedPhotos.push(photoData);
        
        // Auto-set first photo as thumbnail if none exists
        if (!thumbnail && uploadedPhotos.length === 1) {
          setThumbnail(photoData);
        }
        
        // Update progress
        filesProcessed++;
        setUploadProgress(Math.round((filesProcessed / totalFiles) * 100));
      }
      
      // Add the uploaded photos to the existing photos
      setPhotos(prev => [...prev, ...uploadedPhotos]);
      setUploadProgress(100);
      
      // Reset the file input
      e.target.value = '';
    } catch (error) {
      console.error('Error processing photos:', error);
      setUploadError(error.message || 'Failed to process photos');
    } finally {
      setUploading(false);
    }
  };

  // Modified to store logo file in fileStorageRef
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size (max 1MB for logo)
    if (file.size > 1 * 1024 * 1024) {
      setUploadError(`Logo file exceeds the 1MB size limit`);
      return;
    }
    
    // Validate file type - only allow images
    if (!file.type.startsWith('image/')) {
      setUploadError(`Logo file is not a valid image`);
      return;
    }
    
    setUploading(true);
    setUploadError(null);
    
    try {
      // Compress the logo image with a higher quality since logos need crisp edges
      const compressedFile = await compressImage(file);
      
      console.log(`Logo compressed: ${(file.size / 1024).toFixed(2)}KB → ${(compressedFile.size / 1024).toFixed(2)}KB (${Math.round(compressedFile.size / file.size * 100)}%)`);
      
      // Create a local URL for the logo
      const objectUrl = URL.createObjectURL(compressedFile);
      
      // Store the logo file in the fileStorageRef
      fileStorageRef.current.logo = compressedFile;
      
      // Create logo data object with a reference to the file
      const logoData = {
        fileId: 'logo', // Simple ID for logo
        url: objectUrl,
        fileName: file.name,
        fileType: 'image/jpeg', // Always convert to JPEG for consistency
        uploadedAt: new Date().toISOString(),
        needsUpload: true // Flag to indicate this needs to be uploaded to server
      };
      
      // Update state with logo data
      setLogo(logoData);
      
      // Reset the file input
      e.target.value = '';
    } catch (error) {
      console.error('Error processing logo:', error);
      setUploadError(error.message || 'Failed to process logo');
    } finally {
      setUploading(false);
    }
  };


  const handleDeletePhoto = (photoId) => {
    // Find the photo to delete
    const photoToDelete = photos.find(photo => photo.id === photoId);
    
    // Revoke the object URL to prevent memory leaks
    if (photoToDelete && photoToDelete.url && photoToDelete.url.startsWith('blob:')) {
      URL.revokeObjectURL(photoToDelete.url);
    }
    
    if (photoToDelete && photoToDelete.thumbnailUrl && photoToDelete.thumbnailUrl.startsWith('blob:')) {
      URL.revokeObjectURL(photoToDelete.thumbnailUrl);
    }
    
    // Remove the file from fileStorageRef if it exists
    if (photoToDelete && photoToDelete.fileId && fileStorageRef.current.photos[photoToDelete.fileId]) {
      delete fileStorageRef.current.photos[photoToDelete.fileId];
    }
    
    // Remove the photo from the photos array
    const updatedPhotos = photos.filter(photo => photo.id !== photoId);
    setPhotos(updatedPhotos);
    
    // If the deleted photo was the thumbnail, reset the thumbnail
    if (thumbnail && thumbnail.id === photoId) {
      setThumbnail(updatedPhotos.length > 0 ? updatedPhotos[0] : null);
    }
  };
  
  const handleDeleteLogo = () => {
    // Revoke the object URL to prevent memory leaks
    if (logo && logo.url && logo.url.startsWith('blob:')) {
      URL.revokeObjectURL(logo.url);
    }
    
    // Remove the logo from fileStorageRef
    if (fileStorageRef.current.logo) {
      fileStorageRef.current.logo = null;
    }
    
    setLogo(null);
  };
  
  const handleSetAsThumbnail = (photo) => {
    setThumbnail(photo);
  };
  
  // Helper function to compress images
  const compressImage = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          // Detect aspect ratio
          const ratio = img.width / img.height;
          
          // Determine optimal dimensions based on aspect ratio
          let targetWidth, targetHeight;
          let quality = 0.8; // Default quality
          
          // Check for common aspect ratios with a small margin of error
          const isClose = (a, b, margin = 0.05) => Math.abs(a - b) <= margin;
          
          if (isClose(ratio, 1)) {
            // Square (1:1)
            targetWidth = 1080;
            targetHeight = 1080;
          } else if (isClose(ratio, 9/16)) {
            // Vertical (9:16)
            targetWidth = 720;
            targetHeight = 1280;
            quality = 0.85; // Slightly higher quality for vertical
          } else if (isClose(ratio, 16/9)) {
            // Landscape (16:9)
            targetWidth = 1280;
            targetHeight = 720;
          } else if (isClose(ratio, 4/5)) {
            // Portrait (4:5)
            targetWidth = 1080;
            targetHeight = 1350;
            quality = 0.75; // Lower quality for larger portrait images
          } else {
            // Other aspect ratios - calculate dimensions maintaining ratio
            // For landscape orientation
            if (ratio > 1) {
              targetWidth = Math.min(img.width, 1280);
              targetHeight = Math.round(targetWidth / ratio);
            } 
            // For portrait orientation
            else {
              targetHeight = Math.min(img.height, 1350);
              targetWidth = Math.round(targetHeight * ratio);
            }
          }
          
          console.log(`Compressing image: ${file.name} (${img.width}x${img.height}) -> (${targetWidth}x${targetHeight}) at quality ${quality}`);
          
          // Create a canvas with the target dimensions
          const canvas = document.createElement('canvas');
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
          
          // Convert to Blob with desired quality JPEG
          canvas.toBlob(
            (blob) => {
              // Create a new file from the blob
              const compressedFile = new File([blob], file.name, { 
                type: 'image/jpeg',
                lastModified: new Date().getTime()
              });
              
              // Log compression results
              console.log(`Image compressed: ${(file.size / 1024).toFixed(2)}KB → ${(compressedFile.size / 1024).toFixed(2)}KB (${Math.round(compressedFile.size / file.size * 100)}%)`);
              
              resolve(compressedFile);
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };
  
  // Helper function to create thumbnails
  const createThumbnail = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          // Create a small thumbnail version - optimal size for thumbnails is 200x200
          const canvas = document.createElement('canvas');
          canvas.width = 200;
          canvas.height = 200;
          
          const ctx = canvas.getContext('2d');
          
          // Create a center-cropped square thumbnail for consistency
          let sourceX = 0;
          let sourceY = 0;
          let sourceWidth = img.width;
          let sourceHeight = img.height;
          
          // If the image is wider than tall, crop the sides
          if (img.width > img.height) {
            sourceX = (img.width - img.height) / 2;
            sourceWidth = img.height;
          } 
          // If the image is taller than wide, crop the top/bottom
          else {
            sourceY = (img.height - img.width) / 2;
            sourceHeight = img.width;
          }
          
          // Draw the centered, cropped image
          ctx.drawImage(
            img,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            0,
            0,
            200,
            200
          );
          
          // Get the thumbnail as a data URL with lower quality for thumbnails
          const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
          resolve(thumbnailUrl);
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  // Handle drag and drop reordering
  const handleDragStart = (index) => {
    setDraggedPhotoIndex(index);
  };
  
  const handleDragOver = (e, index) => {
    e.preventDefault();
    
    if (draggedPhotoIndex === null || draggedPhotoIndex === index) {
      return;
    }
    
    // Reorder the photos array
    const newPhotos = [...photos];
    const draggedPhoto = newPhotos[draggedPhotoIndex];
    
    // Remove the dragged photo
    newPhotos.splice(draggedPhotoIndex, 1);
    // Add it at the new position
    newPhotos.splice(index, 0, draggedPhoto);
    
    setPhotos(newPhotos);
    setDraggedPhotoIndex(index);
  };
  
  const handleDragEnd = () => {
    setDraggedPhotoIndex(null);
  };
  
  return (
    <div className={`${styles.formSection} ${mode === 'view' ? styles.viewMode : ''}`}>
      <h2 className={styles.sectionTitle}>Photos & Logo</h2>
      <p className={styles.sectionDescription}>
        Upload high-quality promotional photos and your logo. You can add up to 5 photos.
      </p>
      
      {/* Photo upload section */}
      <div className={styles.photoUploader}>
        <h3>Promotional Photos</h3>
        
        {photos.length < 5 && (
          <div
            className={styles.dropZone}
            onClick={triggerPhotoUpload}
          >
            <FontAwesomeIcon icon={faCloudUpload} className={styles.uploadIcon} />
            <p className={styles.uploadText}>Click to upload photos</p>
            <p className={styles.uploadHint}>JPEG or PNG format, max 3MB each (up to 5 photos)</p>
          </div>
        )}
        
        {/* Hidden file input for photos */}
        <input
          type="file"
          ref={photoInputRef}
          style={{ display: 'none' }}
          accept="image/jpeg,image/png"
          multiple
          onChange={handlePhotoUpload}
        />
        
        {/* Upload progress */}
        {uploading && (
          <div className={styles.uploadProgress}>
            <div className={styles.progressBar} style={{ width: `${uploadProgress}%` }}></div>
            <div className={styles.progressText}>Processing... {uploadProgress}%</div>
          </div>
        )}
        
        {/* Upload error */}
        {uploadError && (
          <div className={styles.uploadError}>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{uploadError}</span>
          </div>
        )}
        
        {/* Display uploaded photos */}
      {photos.length > 0 && (
  <div className={styles.photoGrid}>
    {photos.map((photo, index) => (
      <div
        key={photo.id}
        className={styles.photoItem}
        draggable={mode !== 'view'}
        onDragStart={() => handleDragStart(index)}
        onDragOver={(e) => handleDragOver(e, index)}
        onDragEnd={handleDragEnd}
      >
        <img src={photo.url} alt={`Promo ${index + 1}`} className={styles.photoImg} />
        
        <div className={styles.photoActions}>
          <button
            type="button"
            className={`${styles.photoActionBtn} ${thumbnail && thumbnail.id === photo.id ? styles.photoSelectedBtn : ''}`}
            onClick={() => handleSetAsThumbnail(photo)}
            title={thumbnail && thumbnail.id === photo.id ? 'Current thumbnail' : 'Set as thumbnail'}
          >
            <FontAwesomeIcon icon={faStar} />
          </button>
          <button
            type="button"
            className={`${styles.photoActionBtn} ${styles.photoDeleteBtn}`}
            onClick={() => handleDeletePhoto(photo.id)}
            title="Delete photo"
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
        
        {thumbnail && thumbnail.id === photo.id && (
          <div className={styles.thumbnailBadge} title="Thumbnail for EPK">
            <FontAwesomeIcon icon={faStar} />
            <span>Thumbnail</span>
          </div>
        )}
        
        {photo.needsUpload && (
          <div className={styles.uploadPendingBadge} title="Will be uploaded when published">
            <FontAwesomeIcon icon={faCloudUpload} />
            <span>Pending Upload</span>
          </div>
        )}
      </div>
    ))}
  </div>
)}
                
      </div>
      
      {/* Logo upload section */}
      <div className={styles.logoUploader}>
        <h3>Artist/Band Logo</h3>
        
        {!logo ? (
          <div
            className={styles.dropZone}
            onClick={triggerLogoUpload}
          >
            <FontAwesomeIcon icon={faImage} className={styles.uploadIcon} />
            <p className={styles.uploadText}>Click to upload your logo</p>
            <p className={styles.uploadHint}>PNG with transparent background recommended, max 1MB</p>
          </div>
        ) : (
          <div className={styles.logoPreview}>
            <img src={logo.url} alt="Logo" className={styles.logoImg} />
            
            {mode !== 'view' && (
              <button
                type="button"
                className={`${styles.photoActionBtn} ${styles.photoDeleteBtn}`}
                onClick={handleDeleteLogo}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
            
            {logo.needsUpload && (
              <div className={styles.uploadPendingBadge} title="Will be uploaded when published">
                <FontAwesomeIcon icon={faCloudUpload} />
                <span>Pending Upload</span>
              </div>
            )}
          </div>
        )}
        
        {/* Hidden file input for logo */}
        <input
          type="file"
          ref={logoInputRef}
          style={{ display: 'none' }}
          accept="image/jpeg,image/png,image/svg+xml"
          onChange={handleLogoUpload}
        />
      </div>
    </div>
  );
};

export default PhotosStep;